import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { IForm, IModel } from "./abstractions.model";
import { Identifiable } from "./commons.model";

export class Module extends Identifiable implements IModel, IForm {
	private form?: UntypedFormGroup;
	moduleLangId?: number = 0;
	icon?: string = undefined;
	link: string = "";
	order: number = 1;
	name: string = "";
	description: string = "";
	parentModuleId?: number = undefined;
	parentModule?: Module = undefined;
	featureList: Array<Feature> = new Array<Feature>();
	removableFeatureList: Array<Feature> = new Array<Feature>();

	constructor(other?: any) {
		super(other);

		if (other != undefined) {
			this.moduleLangId = other.moduleLangId;
			this.icon = other.icon;
			this.link = other.link;
			this.parentModuleId = other.parentModuleId;
			this.order = other.order;
			this.name = other.name;
			this.description = other.description;
			this.parentModule = (other.parentModule != undefined) ? new Module(other.parentModule) : undefined;
			if (other.featureList != undefined && other.featureList instanceof Array) {
				this.featureList = other.featureList.map((x: any) => new Feature(x));
			}
			if (other.removableFeatureList != undefined && other.removableFeatureList instanceof Array) {
				this.removableFeatureList = other.removableFeatureList.map((x: any) => new Feature(x));
			}
		}
	}
	get asForm(): UntypedFormGroup {
		if (this.form == undefined) {
			let features = new Array<UntypedFormGroup>();
			features = this.featureList.map((x: Feature) => x.asForm);

			this.form = new UntypedFormGroup({
				icon: new UntypedFormControl(this.icon),
				link: new UntypedFormControl(this.link, [Validators.required]),
				parentModuleId: new UntypedFormControl(this.parentModuleId),
				order: new UntypedFormControl(this.order.toString(), [Validators.required, Validators.min(0)]),
				name: new UntypedFormControl(this.name ?? '', [Validators.required, Validators.maxLength(128)]),
				description: new UntypedFormControl(this.description ?? '', [Validators.required, Validators.maxLength(128)]),
				featureList: new UntypedFormArray(features)
			})
		}
		return this.form;
	}
	get fromForm(): Module {
		let value = this.form?.value;

		this.icon = value.icon;
		this.link = value.link;
		this.parentModuleId = (value.parentModule != undefined) ? parseInt(value.parentModule.parentModuleId) : undefined;
		this.order = parseInt(value.order);
		this.name = value.name;
		this.description = value.description;
		this.featureList.forEach((x: Feature) => x.fromForm);

		return this;
	}

	create(): void {
		let feature = new Feature(undefined);
		this.featureList.push(feature);
		if (this.form != null) {
			(this.form.controls.featureList as UntypedFormArray).push(feature.asForm);
		}
	}

	remove(index: number): Feature {
		let feature = this.featureList[index];
		this.removableFeatureList.push(feature);
		this.featureList.splice(index, 1);
		if (this.form != null) {
			(this.form.controls.featureList as UntypedFormArray).removeAt(index);
		}
		return feature;
	}
}

export class Feature extends Identifiable implements IModel, IForm {
	private form?: UntypedFormGroup;
	featureLangId: number = 0;
	moduleId: number = 0;
	featureKey: string = "";
	featureCode: string = "";
	name: string = "";
	description: string = "";
	isRemovable: boolean = false;

	constructor(other?: any) {
		super(other);

		if (other != undefined) {
			this.featureLangId = other.featureLangId;
			this.moduleId = other.moduleId;
			this.featureKey = other.featureKey;
			this.featureCode = other.featureCode;
			this.name = other.name;
			this.description = other.description;
			this.isRemovable = other.isRemovable;
		}
	}

	get asForm(): UntypedFormGroup {
		if (this.form == undefined) {
			this.form = new UntypedFormGroup({
				moduleId: new UntypedFormControl(this.moduleId.toString()),
				featureKey: new UntypedFormControl(this.featureKey),
				featureCode: new UntypedFormControl(this.featureCode),
				name: new UntypedFormControl(this.name ?? '', [Validators.required, Validators.maxLength(128)]),
				description: new UntypedFormControl(this.description ?? '', [Validators.required, Validators.maxLength(128)]),
				isRemovable: new UntypedFormControl(this.isRemovable)
			})
		}
		return this.form;
	}
	get fromForm(): Feature {
		let value = this.form?.value;

		this.moduleId = value.moduleId != undefined ? parseInt(value.moduleId) : 0;
		this.featureKey = value.featureKey;
		this.featureCode = value.featureCode;
		this.name = value.name;
		this.description = value.description;
		this.isRemovable = value.isRemovable;

		return this;
	}
}

export class Profile extends Identifiable implements IModel, IForm {
	private form?: UntypedFormGroup;
	profileLangId: number = 0;
	name: string = "";
	description: string = "";
	featureList: Array<Feature> = new Array<Feature>(); // Lista de todas as funcionalidades do Modulo
	originalProfileModuleFeatureList: Array<ProfileModuleFeature> = new Array<ProfileModuleFeature>(); // Lista de todas as funcionalidades Originais Habilitadas para o Perfil
	profileModuleFeatureList: Array<ProfileModuleFeature> = new Array<ProfileModuleFeature>(); // Lista de todas as funcionalidades Habilitadas para o Perfil
	removableProfileModuleFeatureList: Array<ProfileModuleFeature> = new Array<ProfileModuleFeature>(); // Lista das funcionalidades a serem Removidas do Perfil
	constructor(other?: any) {
		super(other);

		if (other != undefined) {
			this.profileLangId = other.profileLangId;
			this.name = other.name;
			this.description = other.description;
			if (other.profileModuleFeatureList != undefined && other.profileModuleFeatureList instanceof Array) {
				this.originalProfileModuleFeatureList = other.profileModuleFeatureList.map((x: any) => new ProfileModuleFeature(x));
			}
			if (other.profileModuleFeatureList != undefined && other.profileModuleFeatureList instanceof Array) {
				this.profileModuleFeatureList = other.profileModuleFeatureList.map((x: any) => new ProfileModuleFeature(x));
			}
			if (other.removableProfileModuleFeatureList != undefined && other.removableProfileModuleFeatureList instanceof Array) {
				this.removableProfileModuleFeatureList = other.removableProfileModuleFeatureList.map((x: any) => new ProfileModuleFeature(x));
			}
		}
	}

	get asForm(): UntypedFormGroup {
		if (this.form == undefined) {
			let profileModuleFeatures = new Array<UntypedFormGroup>();
			profileModuleFeatures = this.profileModuleFeatureList.map((x: ProfileModuleFeature) => x.asForm);

			this.form = new UntypedFormGroup({
				name: new UntypedFormControl(this.name ?? '', [Validators.required, Validators.maxLength(128)]),
				description: new UntypedFormControl(this.description ?? '', [Validators.required, Validators.maxLength(128)]),
				profileModuleFeatureList: new UntypedFormArray(profileModuleFeatures),
			})
		}
		return this.form;
	}

	get fromForm(): Profile {
		let value = this.form?.value;

		this.name = value.name;
		this.description = value.description;

		return this;
	}

	checkFeature(featureId: number): void {
		let index = this.removableProfileModuleFeatureList.findIndex(x => x.profileId == this.id && x.featureId == featureId);
		if (index > -1)
		{
			this.removableProfileModuleFeatureList.splice(index, 1);
		}

		let pmf = new ProfileModuleFeature();
		pmf.profileId = this.id ?? 0;
		pmf.featureId = featureId;
		this.profileModuleFeatureList.push(pmf);
	}

	uncheckFeature(featureId: number): void {
		let index = this.originalProfileModuleFeatureList.findIndex(x => x.profileId == this.id && x.featureId == featureId);
		if (index > -1)
		{
			let feature = this.profileModuleFeatureList[index];
			this.removableProfileModuleFeatureList.push(feature);
		}
		index = this.profileModuleFeatureList.findIndex(x => x.profileId == this.id && x.featureId == featureId);
		this.profileModuleFeatureList.splice(index, 1);
	}
}

export class ProfileModuleFeature extends Identifiable implements IModel, IForm {
	private form?: UntypedFormGroup;
	profileId: number = 0;
	featureId: number = 0;
	isRemovable: boolean = false;

	constructor(other?: any) {
		super(other);

		if (other != undefined) {
			this.profileId = other.profileId ?? 0;
			this.featureId = other.featureId ?? 0;
			this.isRemovable = other.isRemovable ?? false;
		}
	}

	get asForm(): UntypedFormGroup {
		if (this.form == undefined) {
			this.form = new UntypedFormGroup({
				profileId: new UntypedFormControl(this.profileId.toString()),
				featureId: new UntypedFormControl(this.featureId.toString()),
				isRemovable: new UntypedFormControl(this.isRemovable)
			})
		}
		return this.form;
	}
	
	get fromForm(): ProfileModuleFeature {
		let value = this.form?.value;

		this.profileId = value?.profileId != undefined ? parseInt(value.profileId) : 0;
		this.featureId = value?.featureId != undefined ? parseInt(value.featureId) : 0;
		this.isRemovable = value?.isRemovable != undefined ? value.isRemovable : false;

		return this;
	}
}

export class Firmware extends Identifiable {
	id : number = 0;
    manufacturerId : number = 0;
	manufacturerList : Array<Manufacturer> = new Array<Manufacturer>();
    name: string = "";  

	constructor(other?: any) {
		
		super(other);		
		if (other != undefined) {
			this.id = other.id;
			this.manufacturerId = other.manufacturerId;		
			this.name = other.name;

			if (other.manufacturerList !== undefined && other.manufacturerList instanceof Array && other.manufacturerList.length > 0) {
				this.manufacturerList = other.moduleLangList.map((x: any) => new Manufacturer(x));
			}
		}
	}	
}

export class DeviceStatus extends Identifiable {
	id : number = 0;
    name: string = "";  

	constructor(other?: any) {
		
		super(other);		
		if (other != undefined) {
			this.id = other.id;
			this.name = other.name;
		}
	}
	
}

export class Manufacturer extends Identifiable {
	id : number = 0;
    name: string = "";  

	constructor(other?: any) {		
		super(other);		
		if (other != undefined) {
			this.id = other.id;
			this.name = other.name;
		}
	}
	
}

export class AttributeType extends Identifiable implements IModel, IForm {
    private form?: UntypedFormGroup;

    id: number = 0;
    name: string = "";  
	isMultipleSelection: boolean = false;
	required: boolean = false;
	attributeId?: number = undefined;	
	opttions? :Array<Attribute> = new Array<Attribute>();
	tooltips? : string = undefined;
    ids : Array<number> = new Array<number>();


    constructor(other?: any) {
		super(other);
        if (other !== null) {
            this.id = other.id;
			this.name = other.name;
			this.attributeId = other.attributeId;
			this.opttions = other.opttions;
			this.tooltips = other.id == 1 ? "Segmento do modelo: Rastreador, Long Life,Iscas, Localizador." :
							other.id == 3 ? "Locais de instalação do dispositivo no veículo." :
							other.id == 4 ? "Categoria do equipamento."  :
							other.id == 5 ? "Meio de comunicação utilizado pelo rastreador." :	
							other.id == 6 ? "Barramento de comunição utilizado pelo rastreador com acessórios/dispositivo." :
							other.id == 9 ? "Sistema para navegação utilizado pelo rastreador." 				
							 : "";
			this.isMultipleSelection = other.isMultipleSelection;
			this.required = other.required;
			this.ids = other.ids;
        }
    }

    get asForm(): UntypedFormGroup {
        if (this.form == null) {
            this.form = new UntypedFormGroup({
                id: new UntypedFormControl(this.id),
				attributeId: new UntypedFormControl(this.attributeId),
                name: new UntypedFormControl(this.name, [ Validators.required, Validators.pattern("^[a-zA-Z][a-zA-Z0-9_]*$") ]),
                ids: new UntypedFormControl(this.ids),
				isMultipleSelection:  new UntypedFormControl(this.isMultipleSelection),
            });
        }
        return this.form;
    }

	get fromForm(): AttributeType {
		let value = this.form?.value;

        this.id = value.id;
		this.name=value.name;
		this.attributeId = value.attributeId;

		this.ids= value.ids;

        return this;
    }
}

export class AttributeTypeForm extends Identifiable implements IModel, IForm {
    private form?: UntypedFormGroup;

    id: number = 0;
    name: string = "";  
	isMultipleSelection: boolean = false;
	required: boolean = false;
	attributeId?: number = undefined;	
	opttions? :Array<Attribute> = new Array<Attribute>();
	tooltips? : string = undefined;
    ids : Array<number> = new Array<number>();


    constructor(other?: any) {
		super(other);
        if (other !== null) {
            this.id = other.id;
			this.name = other.name;
			this.attributeId = other.attributeId;
			this.opttions = other.opttions;
			this.tooltips = other.id == 1 ? "Segmento do modelo: Rastreador, Long Life,Iscas, Localizador." :
							other.id == 3 ? "Locais de instalação do dispositivo no veículo." :
							other.id == 4 ? "Categoria do equipamento."  :
							other.id == 5 ? "Meio de comunicação utilizado pelo rastreador." :	
							other.id == 6 ? "Barramento de comunição utilizado pelo rastreador com acessórios/dispositivo." :
							other.id == 9 ? "Sistema para navegação utilizado pelo rastreador." 				
							 : "";
			this.isMultipleSelection = other.isMultipleSelection;
			this.required = other.required;
			this.ids = other.ids;
        }
    }

    get asForm(): UntypedFormGroup {
        if (this.form == null) {
            this.form = new UntypedFormGroup({
                id: new UntypedFormControl(this.id, (this.isMultipleSelection == false && this.required) ? [ Validators.required] : null),
				attributeId: new UntypedFormControl(this.attributeId),
                ids: new UntypedFormControl(this.ids, (this.isMultipleSelection && this.required) ? [ Validators.required] : null),
				isMultipleSelection:  new UntypedFormControl(this.isMultipleSelection),
            });
        }
        return this.form;
    }

	get fromForm(): AttributeTypeForm {
		let value = this.form?.value;

        this.id = value.id;
		this.name=value.name;
		this.attributeId = value.attributeId;

		this.ids= value.ids;

        return this;
    }
}

export class PaymentGroup extends Identifiable implements IModel, IForm {
    private form?: UntypedFormGroup;

    id: number = 0;
    name: string = "";  
    
    constructor(other?: any) {
		super(other);
        if (other !== null) {
            this.id = other.id;
			this.name = other.name;
			
        }
    }

    get asForm(): UntypedFormGroup {
        if (this.form == null) {
            this.form = new UntypedFormGroup({
                id: new UntypedFormControl(this.id),				
                name: new UntypedFormControl(this.name, [ Validators.required, Validators.pattern("^[a-zA-Z][a-zA-Z0-9_]*$") ]),
                
            });
        }
        return this.form;
    }

	get fromForm(): PaymentGroup {
		let value = this.form?.value;

        this.id = value.id;
		this.name=value.name;

        return this;
    }
}

export class Attribute extends Identifiable implements IModel, IForm {
    private form?: UntypedFormGroup;

    id: number = 0;
	attributeTypeId: number = 0;
	attributeStatusId: number = 0;
    name: string = "";  
	note: string = "";  
    
    constructor(other?: any) {
		super(other);
        if (other !== null) {
            this.id = other.id;
			this.name = other.name;
			this.attributeTypeId = other.attributeTypeId;
			this.attributeStatusId = other.attributeStatusId;
			this.note = other.note;
        }
    }

    get asForm(): UntypedFormGroup {
        if (this.form == null) {
            this.form = new UntypedFormGroup({
                id: new UntypedFormControl(this.id),
				attributeTypeId: new UntypedFormControl(this.attributeTypeId),
				attributeStatusId: new UntypedFormControl(this.attributeStatusId),
                name: new UntypedFormControl(this.name, [ Validators.required, Validators.pattern("^[a-zA-Z][a-zA-Z0-9_]*$") ]),
                note: new UntypedFormControl(this.note, [ Validators.required, Validators.pattern("^[a-zA-Z][a-zA-Z0-9_]*$") ]),
               
            });
        }
        return this.form;
    }

	get fromForm(): Attribute {
		let value = this.form?.value;

        this.id = value.id;
		this.name=value.name;
		this.note=value.note;
		this.attributeTypeId=value.attributeTypeId;
		this.attributeStatusId=value.attributeStatusId;
			

        return this;
    }
	update(other: Attribute) {
        this.id = other.id;       
      	this.name= other.name; 

        if (this.form != null) {
            this.form.patchValue(other);
        }
    }

    static createListValue(value: string = ''): UntypedFormGroup {
        return new UntypedFormGroup({
            value: new UntypedFormControl(value)
        });
    }	
}

export class DeviceModelAttribute extends Identifiable implements IModel, IForm {
    private form?: UntypedFormGroup;

    id?: number = 0;
	deviceModelId?: number = 0;
	attributeId?: number = 0;
	//attribute: Attribute = new Attribute();
	name?: string = ""; 
	attributeTypeId?: number = 0; 

	
    constructor(other?: any) {
		super(other);
        if (other !== null) {
            this.id = other.id;
			this.deviceModelId = other.deviceModelId;
			this.attributeId = other.attributeId;
			//this.attribute = (other.attribute != null) ? new Attribute(other.attribute) : new Attribute();
			this.name = other.name;
			this.attributeTypeId = other.attributeTypeId;
			
        }
    }

    get asForm(): UntypedFormGroup {
        if (this.form == null) {
            this.form = new UntypedFormGroup({
				attributeId: new UntypedFormControl(this.attributeId !=0 ? this.attributeId : '' )

            });
        }
        return this.form;
    }

	get fromForm(): DeviceModelAttribute {
		let value = this.form?.value;

		this.attributeId = value.attributeId;		

        return this;
    }
}

export class AccessoryAttribute extends Identifiable implements IModel, IForm {
    private form?: UntypedFormGroup;

    id?: number = 0;
	accessoryId?: number = 0;
	attributeId?: number = 0;
	//attribute: Attribute = new Attribute();
	name?: string = ""; 
	attributeTypeId?: number = 0; 
	cod?: string = ""; 

	
    constructor(other?: any) {
		super(other);
        if (other !== null) {
            this.id = other.id;
			this.accessoryId = other.accessoryId;
			this.attributeId = other.attributeId;
			//this.attribute = (other.attribute != null) ? new Attribute(other.attribute) : new Attribute();
			this.name = other.name;
			this.cod = other.cod;
			this.attributeTypeId = other.attributeTypeId;
			
        }
    }

    get asForm(): UntypedFormGroup {
        if (this.form == null) {
            this.form = new UntypedFormGroup({
				attributeId: new UntypedFormControl(this.attributeId != 0 ? this.attributeId : "")

            });
        }
        return this.form;
    }

	get fromForm(): AccessoryAttribute {
		let value = this.form?.value;

		this.attributeId = value.attributeId;		

        return this;
    }
}

export class PaymentGroupAccessory extends Identifiable implements IModel, IForm {
    private form?: UntypedFormGroup;

    id?: number = 0;
	accessoryId?: number = 0;
	paymentGroupId?: number = 0;

	
    constructor(other?: any) {
		super(other);
        if (other !== null) {
            this.id = other.id;
			this.accessoryId = other.accessoryId;
			this.paymentGroupId = other.paymentGroupId;
						
        }
    }

    get asForm(): UntypedFormGroup {
        if (this.form == null) {
            this.form = new UntypedFormGroup({
				paymentGroupId: new UntypedFormControl(this.paymentGroupId !=0 ? this.paymentGroupId : '' )

            });
        }
        return this.form;
    }

	get fromForm(): PaymentGroupAccessory {
		let value = this.form?.value;

		this.paymentGroupId = value.paymentGroupId;		

        return this;
    }
}

export class AccessoryAttachment extends Identifiable implements IModel, IForm {
    private form?: UntypedFormGroup;

	id : number = 0;
    accessoryId:  number = 0;
	filename: string = "";
	crc?: string = undefined;
	fileLentgh?:   string = undefined;
	description?: string = undefined;
	urlFile?: string = undefined;
	contentType?: string = undefined;



	constructor(other?: any) {		
		super(other);		
		if (other != undefined) {
			this.id = other.id;
			this.accessoryId = other.accessoryId;
			this.filename = other.filename;
			this.crc = other.crc;
			this.fileLentgh = other.fileLentgh;
			this.description = other.description;
			this.urlFile = other.urlFile;
			this.contentType = other.contentType;
		}
	}

	get asForm(): UntypedFormGroup {
        if (this.form == null) {
            this.form = new UntypedFormGroup({
                id: new UntypedFormControl(this.id),
				accessoryId: new UntypedFormControl(this.accessoryId),
				filename: new UntypedFormControl(this.filename),
				crc: new UntypedFormControl(this.crc),
				fileLentgh: new UntypedFormControl(this.fileLentgh),
				description: new UntypedFormControl(this.description),
				urlFile: new UntypedFormControl(this.urlFile),
				contentType: new UntypedFormControl(this.contentType),
				

            });
        }
        return this.form;
    }

	get fromForm(): AccessoryAttachment {
		let value = this.form?.value;

        this.id = value.id;
		this.accessoryId = value.this.accessoryId;
		this.filename = value.filename;
		this.crc = value.crc;
		this.fileLentgh = value.fileLentgh;
		this.description = value.description;
		this.urlFile = value.urlFile;
		this.contentType = value.contentType;

        return this;
    }	
	
}

export class LocationInstallation extends Identifiable implements IModel, IForm {
    private form?: UntypedFormGroup;

    id: number = 0;
	name: string = "";  
    
    constructor(other?: any) {
		super(other);
        if (other !== null) {
            this.id = other.id;
			this.name = other.name;			
        }
    }

    get asForm(): UntypedFormGroup {
        if (this.form == null) {
            this.form = new UntypedFormGroup({
                id: new UntypedFormControl(this.id),
				name: new UntypedFormControl(this.name, [ Validators.required, Validators.pattern("^[a-zA-Z][a-zA-Z0-9_]*$") ]),

            });
        }
        return this.form;
    }

	get fromForm(): LocationInstallation {
		let value = this.form?.value;

        this.id = value.id;
		this.name=value.name;

        return this;
    }	
}

export class DeviceModel extends Identifiable implements IModel, IForm {
	private form?: UntypedFormGroup;

	name: string = "";  
	manufacturerId : number = 0;
	manufacturer?: string = undefined; 
	firmwareId  : number = 0;
	firmware?: string = undefined; 
	hasLine: boolean = false; 
	isPermanentItem: boolean = false; 
	inputs: number = 0;
	outputs : number = 0;
	hasBackupBattery: boolean = false;
	deviceStatusId: number = 0;
	deviceStatus?: string = undefined; 
	note?: string = undefined; 
	isDeleted: boolean = false;
	lastDate?: Date = undefined;

	deviceModelAttributeList:  Array<DeviceModelAttribute> = new  Array<DeviceModelAttribute>();
	removabledeviceModelAttributeList:  Array<DeviceModelAttribute> = new  Array<DeviceModelAttribute>();

	attributesList:  Array<AttributeTypeForm> = new  Array<AttributeTypeForm>();

	constructor(other?: any) {
		super(other);

		if (other != undefined) {
			this.id = other.id;
			this.name = other.name;
			this.manufacturerId = other.manufacturerId;
			this.manufacturer = other.manufacturer; 
			this.firmwareId = other.firmwareId;
			this.firmware = other.firmware; 
			this.hasLine = other.hasLine; 
			this.isPermanentItem = other.isPermanentItem; 
			this.inputs = other.inputs;
			this.outputs = other.outputs;
			this.hasBackupBattery = other.hasBackupBattery;
			this.deviceStatusId = other.deviceStatusId;
			this.deviceStatus = other.deviceStatus;
			this.note = other.note;
			this.isDeleted = other.isDeleted;
			this.lastDate = other.lastDate;

			if (other.deviceModelAttributeList !== undefined && other.deviceModelAttributeList instanceof Array && other.deviceModelAttributeList.length > 0) {
				this.deviceModelAttributeList = other.deviceModelAttributeList.map((x: DeviceModelAttribute) => new DeviceModelAttribute(x));
			}

			if (other.removabledeviceModelAttributeList !== undefined && other.removabledeviceModelAttributeList instanceof Array && other.removabledeviceModelAttributeList.length > 0) {
				this.removabledeviceModelAttributeList = other.removabledeviceModelAttributeList.map((x: DeviceModelAttribute) => new DeviceModelAttribute(x));
			}

		}
	}
	updateAttributesType(attributes : Array<AttributeTypeForm>)
	{
		attributes.forEach(x=> {			
			if(this.deviceModelAttributeList.some(a => a.attributeTypeId == x.id))
			{
				let valor: Array<number> = new Array<number>();
				let val : number = 0;
				this.deviceModelAttributeList.filter(a => a.attributeTypeId == x.id).forEach(d =>
					{
						if(d.attributeId != undefined){
							valor.push(d.attributeId);
							val = d.attributeId;
						}
					}
				);
				if(x.isMultipleSelection)
					x.ids = valor;
				else
					x.attributeId = val;
			}	
		});
		this.attributesList = attributes;
	}

	


	get asForm(): UntypedFormGroup {
		if (this.form == undefined) {

			let atts : Array<UntypedFormGroup> = new Array<UntypedFormGroup>();

			if(this.attributesList.length > 0)
			{				
				atts = this.attributesList.map((x: AttributeTypeForm) => x.asForm);
			}

			this.form = new UntypedFormGroup({
				manufacturerId: new UntypedFormControl(this.manufacturerId !=  0 ? this.manufacturerId : ''  , [Validators.required]),
				firmwareId: new UntypedFormControl(this.firmwareId !=  0 ? this.firmwareId : '',[Validators.required]),
				deviceStatusId: new UntypedFormControl(this.deviceStatusId !=  0 ? this.deviceStatusId : '',[Validators.required]),
				hasLine: new UntypedFormControl(this.hasLine, [Validators.required]),		
				hasBackupBattery: new UntypedFormControl(this.hasBackupBattery, [Validators.required]),			
				isPermanentItem: new UntypedFormControl(this.isPermanentItem,[Validators.required]),				
				inputs: new UntypedFormControl(this.inputs,[Validators.required, Validators.min(0), Validators.max(50) ]),
				outputs: new UntypedFormControl(this.outputs,[Validators.required, Validators.min(0), Validators.max(50) ]),				
				name: new UntypedFormControl(this.name ?? '',[Validators.required, Validators.maxLength(240)]),
				note: new UntypedFormControl(this.note ?? '',[Validators.maxLength(500)]),
				attributesList : new UntypedFormArray(atts)
			})
		}
		return this.form;
	}

	get fromForm(): DeviceModel {
		let value = this.form?.value;

		this.manufacturerId = value.manufacturerId;
		this.firmwareId = value.firmwareId;
		this.deviceStatusId= value.deviceStatusId;
		this.hasLine= value.hasLine;
		this.isPermanentItem= value.isPermanentItem	;	
		this.inputs= value.inputs;
		this.outputs= value.outputs;
		this.name = value.name;
		this.note = value.note;		
		this.hasBackupBattery = value.hasBackupBattery;

		let aal : Array<DeviceModelAttribute> = new  Array<DeviceModelAttribute>();

		debugger;

		value.attributesList.forEach((x : AttributeType) => {
			if(x.isMultipleSelection == true)
			{
				x.ids.forEach(i => {
					if(!this.deviceModelAttributeList.some(a => a.attributeId == i))
					{
						let dma: DeviceModelAttribute = new DeviceModelAttribute( { 
							id : 0, 
							accessoryId : this.id,
							attributeId : i
						});	
						aal.push(dma);
					}
					else{
						let dma: DeviceModelAttribute | undefined = this.deviceModelAttributeList.filter(a => a.attributeId == i).pop();
							if(dma != undefined)
								aal.push(dma);
					}
				});
			}
			else{
				if(!this.deviceModelAttributeList.some(a => a.attributeId == x.attributeId))
					{
						let dma: DeviceModelAttribute = new DeviceModelAttribute( { 
							id : 0, 
							accessoryId : this.id,
							attributeId : x.attributeId
						});	
						aal.push(dma);
					}
					else{
						let dma: DeviceModelAttribute | undefined = this.deviceModelAttributeList.filter(a => a.attributeId == x.attributeId).pop();
							if(dma != undefined)
								aal.push(dma);
					}
			}
		});
		
		this.removabledeviceModelAttributeList  = new  Array<DeviceModelAttribute>();
		
		debugger;

		this.deviceModelAttributeList.forEach(x => {
			if(!aal.some(a => a.attributeId == x.attributeId))
			{
				this.removabledeviceModelAttributeList.push(x);
			}			
		});

		this.deviceModelAttributeList = aal;
		

		return this;
	}
}

export class  LocationInstallationAccessory extends Identifiable implements IModel, IForm {
    private form?: UntypedFormGroup;

    id: number = 0;
	locationInstallationId: number = 0;
	accessoryId: number = 0;
    
    constructor(other?: any) {
		super(other);
        if (other !== null) {
            this.id = other.id;		
			this.locationInstallationId = other.locationInstallationId;
			this.accessoryId = other.accessoryId;
        }
    }

    get asForm(): UntypedFormGroup {
        if (this.form == null) {
            this.form = new UntypedFormGroup({
                id: new UntypedFormControl(this.id),
				locationInstallationId: new UntypedFormControl(this.locationInstallationId),
				accessoryId: new UntypedFormControl(this.accessoryId),               
            });
        }
        return this.form;
    }

	get fromForm(): LocationInstallationAccessory {
		let value = this.form?.value;

        this.id = value.id;
		this.locationInstallationId=value.locationInstallationId;
		this.accessoryId=value.accessoryId;
			

        return this;
    }
	   
}

export class Accessory extends Identifiable implements IModel, IForm {
	private form?: UntypedFormGroup;

	name: string = "";
	manufacturerId: number = 0;
	manufacturer?: string = undefined;
  	sapCod?: string = undefined; 
 	serializedItem: boolean = false; 
  	consumable: boolean = false; 
  	firmwareId: number = 0;
  	firmware?: string = undefined;
  	hasBackupBattery: boolean = false; 
  	inputs: number = 0;
  	outputs: number = 0;
 	deviceStatusId: number = 0;
  	deviceStatus?: string = undefined;
  	note?: string = undefined;
  	isDeleted: boolean = false;
	lastDate?: Date = undefined;
	accessoryType?: string = undefined;
	accessoryGroup?: string = undefined;
    paymentGroup?: string = undefined;

	accessoryAttributeList:  Array<AccessoryAttribute> = new  Array<AccessoryAttribute>();
	removableAccessoryAttributeList:  Array<AccessoryAttribute> = new  Array<AccessoryAttribute>();

	attributesList:  Array<AttributeTypeForm> = new  Array<AttributeTypeForm>();

	paymentGroupAccessoryList:  Array<PaymentGroupAccessory> = new  Array<PaymentGroupAccessory>();	
	removablePaymentGroupAccessoryList:  Array<PaymentGroupAccessory> = new  Array<PaymentGroupAccessory>();

	accessoryAttachmentList: Array<AccessoryAttachment> = new Array<AccessoryAttachment>();
	
	locationInstallationAccessoryList:  Array<LocationInstallationAccessory> = new  Array<LocationInstallationAccessory>();
	removableLocationInstallationAccessoryList:  Array<LocationInstallationAccessory> = new  Array<LocationInstallationAccessory>();
	locationInstallationAccessoryExceptionList:  Array<LocationInstallationAccessory> = new  Array<LocationInstallationAccessory>();
	removableLocationInstallationAccessoryExceptionList:  Array<LocationInstallationAccessory> = new  Array<LocationInstallationAccessory>();

	constructor(other?: any) {
		super(other);

		if (other != undefined) {
			this.id = other.id;
			this.name = other.name;
			this.manufacturerId = other.manufacturerId;
			this.manufacturer = other.manufacturer;
			this.sapCod = other.sapCod;
			this.serializedItem = other.serializedItem;
			this.consumable = other.consumable;
			this.firmwareId = other.firmwareId;
			this.firmware = other.firmware;
			this.hasBackupBattery = other.hasBackupBattery;
			this.inputs = other.inputs;
			this.outputs = other.outputs;
			this.deviceStatusId = other.deviceStatusId;
			this.deviceStatus = other.deviceStatus;
			this.note = other.note;
			this.isDeleted = other.isDeleted;
			this.lastDate = other.lastDate;
			this.accessoryType = other.accessoryType;
			this.accessoryGroup = other.accessoryGroup;
			this.paymentGroup = other.paymentGroup;

			if (other.accessoryAttributeList !== undefined && other.accessoryAttributeList instanceof Array && other.accessoryAttributeList.length > 0) {
				this.accessoryAttributeList = other.accessoryAttributeList.map((x: AccessoryAttribute) => new AccessoryAttribute(x));
			
				this.accessoryGroup = ""
				this.accessoryType = ""

				this.accessoryAttributeList.filter(x => x.cod == 'AccessoryType').forEach(x =>
				{
					this.accessoryType += (this.accessoryType != undefined && this.accessoryType?.length > 1) ? ", " : "";
					this.accessoryType += x.name ?? "";
				});
				this.accessoryAttributeList.filter(x => x.cod == 'AccessoryGroup').forEach(x =>
				{
					this.accessoryGroup += (this.accessoryGroup != undefined && this.accessoryGroup?.length > 1) ? ", " : "";
					this.accessoryGroup += x.name ?? "";
				});
			}

			if (other.accessoryAttachmentList !== undefined && other.accessoryAttachmentList instanceof Array && other.accessoryAttachmentList.length > 0) {
				this.accessoryAttachmentList = other.accessoryAttachmentList.map((x: AccessoryAttachment) => new AccessoryAttachment(x));
			}


			
			if (other.removableAccessoryAttributeList !== undefined && other.removableAccessoryAttributeList instanceof Array && other.removableAccessoryAttributeList.length > 0) {
				this.removableAccessoryAttributeList = other.removableAccessoryAttributeList.map((x: AccessoryAttribute) => new AccessoryAttribute(x));
			}

			if (other.paymentGroupAccessoryList !== undefined && other.paymentGroupAccessoryList instanceof Array && other.paymentGroupAccessoryList.length > 0) {
				this.paymentGroupAccessoryList = other.paymentGroupAccessoryList.map((x: PaymentGroupAccessory) => new PaymentGroupAccessory(x));
			}

			if (other.removablePaymentGroupAccessoryList !== undefined && other.removablePaymentGroupAccessoryList instanceof Array && other.removablePaymentGroupAccessoryList.length > 0) {
				this.removablePaymentGroupAccessoryList = other.removablePaymentGroupAccessoryList.map((x: PaymentGroupAccessory) => new PaymentGroupAccessory(x));
			}

			if (other.locationInstallationAccessoryList !== undefined && other.locationInstallationAccessoryList instanceof Array && other.locationInstallationAccessoryList.length > 0) {
				this.locationInstallationAccessoryList = other.locationInstallationAccessoryList.map((x: LocationInstallationAccessory) => new LocationInstallationAccessory(x));
			}

			if (other.removableLocationInstallationAccessoryList !== undefined && other.removableLocationInstallationAccessoryList instanceof Array && other.removableLocationInstallationAccessoryList.length > 0) {
				this.removableLocationInstallationAccessoryList = other.removableLocationInstallationAccessoryList.map((x: LocationInstallationAccessory) => new LocationInstallationAccessory(x));
			}

			if (other.locationInstallationAccessoryExceptionList !== undefined && other.locationInstallationAccessoryExceptionList instanceof Array && other.locationInstallationAccessoryExceptionList.length > 0) {
				this.locationInstallationAccessoryExceptionList = other.locationInstallationAccessoryExceptionList.map((x: LocationInstallationAccessory) => new LocationInstallationAccessory(x));
			}

			if (other.removableLocationInstallationAccessoryExceptionList !== undefined && other.removableLocationInstallationAccessoryExceptionList instanceof Array && other.removableLocationInstallationAccessoryExceptionList.length > 0) {
				this.removableLocationInstallationAccessoryExceptionList = other.removableLocationInstallationAccessoryExceptionList.map((x: LocationInstallationAccessory) => new LocationInstallationAccessory(x));
			}
		}
	}

	updateAttributesType(attributes : Array<AttributeTypeForm>)
	{
		attributes.forEach(x=> {			
			if(this.accessoryAttributeList.some(a => a.attributeTypeId == x.id))
			{
				let valor: Array<number> = new Array<number>();
				let val : number = 0;
				this.accessoryAttributeList.filter(a => a.attributeTypeId == x.id).forEach(d =>
					{
						if(d.attributeId != undefined){
							valor.push(d.attributeId);
							val = d.attributeId;
						}
					}
				);
				if(x.isMultipleSelection)
					x.ids = valor;
				else
					x.attributeId = val;
			}	
		});
		this.attributesList = attributes;
	}


	get asForm(): UntypedFormGroup {
		if (this.form == undefined) {

			let atts : Array<UntypedFormGroup> = new Array<UntypedFormGroup>();

			if(this.attributesList.length > 0)
			{				
				atts = this.attributesList.map((x: AttributeTypeForm) => x.asForm);
			}

			let paymentGroupId : Array<number> = new Array<number>();

			if(this.paymentGroupAccessoryList.length > 0)
			{
				this.paymentGroupAccessoryList.forEach( ( x : PaymentGroupAccessory) => {
					if(x.paymentGroupId != undefined)
						paymentGroupId.push(x.paymentGroupId);
				});
			}

			let locationInstallationAccessoryId : Array<number> = new Array<number>();

			if(this.locationInstallationAccessoryList.length > 0)
			{	this.locationInstallationAccessoryList.forEach( ( x : LocationInstallationAccessory) => {
					if(x.locationInstallationId != undefined)
						locationInstallationAccessoryId.push(x.locationInstallationId);
				});
			}

			let locationInstallationAccessoryExceptionid : Array<number> = new Array<number>();

			if(this.locationInstallationAccessoryExceptionList.length > 0)
			{		
				this.locationInstallationAccessoryExceptionList.forEach( ( x : LocationInstallationAccessory) => {
					if(x.locationInstallationId != undefined)
					locationInstallationAccessoryExceptionid.push(x.locationInstallationId);
				});
			}

			this.form = new UntypedFormGroup({
				
				name: new UntypedFormControl(this.name ?? '',[Validators.required, Validators.maxLength(240)]),
				manufacturerId: new UntypedFormControl(this.manufacturerId !=  0 ? this.manufacturerId : ''  , [Validators.required]),
				sapCod: new UntypedFormControl(this.sapCod ?? '',[Validators.required, Validators.maxLength(50)]),
				serializedItem: new UntypedFormControl(this.serializedItem,[Validators.required]),		
				consumable: new UntypedFormControl(this.consumable,[Validators.required]),				
				firmwareId: new UntypedFormControl(this.firmwareId !=  0 ? this.firmwareId : ''),
				hasBackupBattery: new UntypedFormControl(this.hasBackupBattery, [Validators.required]),			
				inputs: new UntypedFormControl(this.inputs,[Validators.required, Validators.min(0), Validators.max(50) ]),
				outputs: new UntypedFormControl(this.outputs,[Validators.required, Validators.min(0), Validators.max(50) ]),				
				deviceStatusId: new UntypedFormControl(this.deviceStatusId !=  0 ? this.deviceStatusId : '',[Validators.required]),
				note: new UntypedFormControl(this.note ?? '',[Validators.maxLength(500)]),
				paymentGroupId : new UntypedFormControl(paymentGroupId),
				attributesList : new UntypedFormArray(atts),
				locationInstallationAccessoryId : new UntypedFormControl(locationInstallationAccessoryId),
				locationInstallationAccessoryExceptionid : new UntypedFormControl(locationInstallationAccessoryExceptionid)
			})	
			
		}
		return this.form;
	}

	get fromForm(): Accessory {
		let value = this.form?.value;

		this.name = value.name;
		this.manufacturerId = value.manufacturerId;
		this.sapCod = value.sapCod;
		this.serializedItem= value.serializedItem;
		this.consumable= value.consumable;
		this.firmwareId = value.firmwareId;
		this.hasBackupBattery= value.hasBackupBattery;
		this.inputs= value.inputs;
		this.outputs= value.outputs;
		this.deviceStatusId= value.deviceStatusId;
		this.note = value.note;	

		let aal : Array<AccessoryAttribute> = new  Array<AccessoryAttribute>();

		value.attributesList.forEach((x : AttributeType) => {
			if(x.isMultipleSelection == true)
			{
				x.ids.forEach(i => {
					if(!this.accessoryAttributeList.some(a => a.attributeId == i))
					{
						let dma: AccessoryAttribute = new AccessoryAttribute( { 
							id : 0, 
							accessoryId : this.id,
							attributeId : i
						});	
						aal.push(dma);
					}
					else{
						let dma: AccessoryAttribute | undefined = this.accessoryAttributeList.filter(a => a.attributeId == i).pop();
							if(dma != undefined)
								aal.push(dma);
					}
				});
			}
			else{
				if(!this.accessoryAttributeList.some(a => a.attributeId == x.attributeId))
					{
						let dma: AccessoryAttribute = new AccessoryAttribute( { 
							id : 0, 
							accessoryId : this.id,
							attributeId : x.attributeId
						});	
						aal.push(dma);
					}
					else{
						let dma: AccessoryAttribute | undefined = this.accessoryAttributeList.filter(a => a.attributeId == x.attributeId).pop();
							if(dma != undefined)
								aal.push(dma);
					}
			}
		});

		this.removableAccessoryAttributeList  = new  Array<AccessoryAttribute>();

		this.accessoryAttributeList.forEach(x => {
			if(!aal.some(a => a.attributeId == x.attributeId))
			{
				this.removableAccessoryAttributeList.push(x);
			}			
		});

		let lial : Array<LocationInstallationAccessory> = new  Array<LocationInstallationAccessory>();

		value.locationInstallationAccessoryId.forEach((x : number) => {
			if(!this.locationInstallationAccessoryList.some(a => a.locationInstallationId == x))
			{
				let dma: LocationInstallationAccessory = new LocationInstallationAccessory( { 
					id : 0, 
					accessoryId : this.id,
					locationInstallationId : x,
				});	
				lial.push(dma);
			}
			else
			{

				let dma: LocationInstallationAccessory | undefined = this.locationInstallationAccessoryList.filter(a => a.locationInstallationId == x).pop();
					if(dma != undefined)
					lial.push(dma);
			}	
		});

		this.removableLocationInstallationAccessoryList  = new  Array<LocationInstallationAccessory>();

		this.locationInstallationAccessoryList.forEach(x => {
			if(!lial.some(a => a.locationInstallationId== x.locationInstallationId))
			{
				this.removableLocationInstallationAccessoryList.push(x);
			}			
		});
		

		let liael : Array<LocationInstallationAccessory> = new  Array<LocationInstallationAccessory>();

		value.locationInstallationAccessoryExceptionid.forEach((x : number) => {
			if(!this.locationInstallationAccessoryExceptionList.some(a => a.locationInstallationId == x))
			{
				let dma: LocationInstallationAccessory = new LocationInstallationAccessory( { 
					id : 0, 
					accessoryId : this.id,
					locationInstallationId : x,
				});	
				liael.push(dma);
			}
			else
			{
				let dma: LocationInstallationAccessory | undefined = this.locationInstallationAccessoryList.filter(a => a.locationInstallationId == x).pop();
					if(dma != undefined)
					liael.push(dma);
			}	
		});

		this.removableLocationInstallationAccessoryExceptionList  = new  Array<LocationInstallationAccessory>();

		this.locationInstallationAccessoryExceptionList.forEach(x => {
			if(!liael.some(a => a.locationInstallationId== x.locationInstallationId))
			{
				this.removableLocationInstallationAccessoryExceptionList.push(x);
			}			
		});
		

		let pgal : Array<PaymentGroupAccessory> = new  Array<PaymentGroupAccessory>();

		value.paymentGroupId.forEach((x : number) => {
			if(!this.paymentGroupAccessoryList.some(a => a.paymentGroupId == x))
			{
				let dma: PaymentGroupAccessory = new PaymentGroupAccessory( { 
					id : 0, 
					accessoryId : this.id,
					paymentGroupId : x,
				});	
				pgal.push(dma);
			}
			else
			{

				let dma: PaymentGroupAccessory | undefined = this.paymentGroupAccessoryList.filter(a => a.paymentGroupId == x).pop();
					if(dma != undefined)
						pgal.push(dma);
			}	
		});

		this.removablePaymentGroupAccessoryList  = new  Array<PaymentGroupAccessory>();

		this.paymentGroupAccessoryList.forEach(x => {
			if(!pgal.some(a => a.paymentGroupId == x.paymentGroupId))
			{
				this.removablePaymentGroupAccessoryList.push(x);
			}			
		});
		
		this.paymentGroupAccessoryList = pgal;
		this.accessoryAttributeList = aal;
		this.locationInstallationAccessoryList = lial;
		this.locationInstallationAccessoryExceptionList = liael;
		return this;
	}
}